<template>
    <v-form ref="form">
        <v-input v-model="students" :rules="[v => v.length ? true : 'At least one student is required', v => v.length > studentLimit ? `You've exceeded the limit of ${studentLimit} students for one intervention plan. Remove students to continue.` : true]" hide-details="auto">
            <template #message="{ message }">
                <banner style="margin-bottom: 16px;" usage="warning" :text="message" />
            </template>
        </v-input>

        <div v-if="!(fastbridgeMonitor && fastbridgeMonitor.students)">
            <fe-label>School Year</fe-label>
            <fe-remote-combo
                :url="$models.getUrl('schoolYear', 'read')"
                rootProperty="years"
                v-model="schoolYear"
                :rules="$fieldValidators('select', 'School year', { required: true })"
                data-test="create-intervention-students-school-year"
            />
        </div>

        <div v-if="!(fastbridgeMonitor && fastbridgeMonitor.students)">
            <fe-label>School (Optional)</fe-label>
            <fe-remote-combo
                :disabled="!schoolYear"
                :items="schools"
                rootProperty="schools"
                v-model="school"
                data-test="create-intervention-students-school"
            />
        </div>

        <div data-test="student-search">
            <fe-label v-if="!(fastbridgeMonitor && fastbridgeMonitor.students) || (fastbridgeMonitor.students && students.length < fastbridgeMonitor.students.length)">Select Students</fe-label>
            <student-name-search v-if="!(fastbridgeMonitor && fastbridgeMonitor.students)"
                data-test="create-intervention-students-search" 
                :maximumStudentsAllowed="studentLimit" 
                :currentCount="students.length" 
                clearOnSelect includeAdvanced 
                v-model="studentSearch" 
                :ignoredStudents="students" 
                :schoolYearId="schoolYearId" 
                :schoolId="schoolId" 
            />
            <fe-remote-combo v-else-if="fastbridgeMonitor.students && students.length < fastbridgeMonitor.students.length"
                data-test="create-intervention-students-search-fb"
                :items="getFbStudents"
                v-model="studentSearch"
            >
                <template v-slot:item="data">
                    <v-list-item-content>
                        <v-list-item-title v-safe-html="data.item.student_full_name" />
                    </v-list-item-content>
                </template>
            </fe-remote-combo>
        </div>

        <div class="section-title mb-2" v-if="students.length">{{ students.length }} Student{{ students.length !== 1 ? 's' : '' }} Added</div>

        <div class="d-flex" style="flex-wrap:wrap;">
            <div v-for="(student, i) in namedStudents" class="added-student d-flex mr-2 mb-2" :key="`student-`+i">
                <div class="student-name">{{student.student_full_name}}</div>
                <div class="ml-auto">
                    <fe-icon-btn small useIcon="fal fa-times" @click="removeStudent(student)" data-test="create-intervention-students-remove-student" />
                </div>
            </div>
        </div>
        <div class="d-flex card-bottom" :class="students.length || error ? 'mt-5' : ''">
            <div v-if="fastbridgeMonitor && fastbridgeMonitor.students && fastbridgeMonitor.unmatched_student_count > 0" class="d-flex ml-5" style="color: #F02D1F">
                <v-icon class="mr-2" style="font-size: 13px; color: #F02D1F;">fas fa-exclamation-triangle</v-icon>
                {{ fastbridgeMonitor && fastbridgeMonitor.unmatched_student_count ? fastbridgeMonitor.unmatched_student_count : 0 }} unmatched student 
                {{ fastbridgeMonitor && fastbridgeMonitor.unmatched_student_count ? fastbridgeMonitor.unmatched_student_count > 1 ? 's' : null : null}}
            </div>
            <fe-btn @click="nextStep" data-test="create-intervention-students-step-add">Continue</fe-btn>
        </div>
    </v-form>
</template>

<script>
    import { mapState } from 'vuex'
    import StudentNameSearch from '@/components/common/StudentNameSearch'
    import Banner from '@/components/common/Banner'

    export default {
        name: 'InterventionStudents',
        components: {
            StudentNameSearch,
            Banner,
        },
        props: {
            value: {
                type: Array,
                default: () => []
            },
            fastbridgeMonitor: {
                type: Object,
                required: false,
                default: () => {}
            },
            preSelectedStudents: {
                type: Array,
                default: () => []
            },
        },
        computed: {
            ...mapState('global', ['currentYear', 'defaultSchool']),
            studentLimit() {
                return 35 // BE note: maxStudentsAllowedWithIntervention
            },
            school: {
                get () {
                    return this.$store.state.intervention.school
                },
                set (v) {
                    this.$store.commit('intervention/set', {
                        key: 'school',
                        value: v
                    })
                }
            },
            isValid() {
                return this.students.length > 0
            },
            getFbStudents() {
                // get students that are in fb monitor but not in selected students list
                return this.fastbridgeMonitor.students.filter(fbStudent => {
                    return !this.students.some(student => {
                        return fbStudent.student_id === student.student_id
                    })
                })
            },
            schoolYearId() {
                return this.schoolYear?.id
            },
            schoolId() {
                return this.school?.id
            },
            namedStudents() {
                return this.students.filter(student => student.student_full_name)
            }
        },
        data() {
            return {
                students: [],
                schoolYear: null,
                schools: [],
                studentSearch: null,
                error: false,
            }
        },
        created() {
            this.students =  this.value
            this.schoolYear = this.currentYear

            // FastBridge check
            // Should data come from FastBridge progress monitor?
            // Mark all students as selected and let user remove as needed
            if (this.fastbridgeMonitor?.students) {
                this.students = this.$_.cloneDeep(this.fastbridgeMonitor?.students)
            } else if (this.preSelectedStudents.length) {
                this.students = this.$_.cloneDeep(this.preSelectedStudents)
            }

            // set default school if not a school group
            if(this.defaultSchool?.id && !this.defaultSchool?.school_group_id) this.school = this.defaultSchool
        },
        watch: {
            schoolYear(v) {
                this.$store.commit('intervention/set', {
                    key: 'schoolYear',
                    value: v
                })
                if (!v) return

                this.$modelGet('school', { school_year_id: v ? v.id : null })
                    .then(response => { this.schools = response })
            },
            studentSearch(v) {
                if (v) {
                    const studentList = Array.isArray(v) ? v : [v];
                    if (studentList.some(s => s.school_id != this.school?.id)) {
                        // clear school if there's a student from a different school
                        this.school = null
                    }
                    this.students.push(...studentList)
                    this.$nextTick(() => {
                        this.studentSearch = null
                    })
                }
            },
            students: {
                deep: true,
                handler() {
                    this.$emit('input', this.students)
                }
            },
        },
        methods: {
            removeStudent(s) {
                let i = this.students.findIndex(rec => rec.student_id === s.student_id)
                this.students.splice(i, 1)
            },
            nextStep() {
                if (this.$refs.form.validate()) {
                    this.error = false
                    this.$emit('go')
                } else {
                    this.error = true
                    this.$emit('scrollToTop')
                }
            },
        }
    }
</script>

<style lang="scss" scoped>
.added-student {
    background: #ECEDF1;
    border-radius: 4px;
    height: 30px;
    width: fit-content;
    ::v-deep i {
        height: 20px !important;
        width: 20px !important;
        font-size: 14px !important;
    }
    ::v-deep .v-btn {
        margin: 2px !important;
    }

    .student-name {
        line-height: 30px;
        padding-left: 16px;
    }

}
</style>
